import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { updateSearchRateLimitConfigRequest } from 'domains/searchRateLimit/SearchRateLimit.store';
import {
  SearchRateLimitConfig,
  UpdateSearchRateLimitConfigRequest,
} from 'domains/searchRateLimit/SearchRateLimit.types';
import { Flex, InputNumber, MarginBox, Text, WhiteButton, YellowButton } from 'UI';

const SUCCESS_FEEDBACK_DELAY_MS = 500;

interface CountryRateLimitFormProps {
  countryCode: string;
  config: SearchRateLimitConfig | null;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const CountryRateLimitForm: React.FC<CountryRateLimitFormProps> = ({
  countryCode,
  config,
  onSuccess,
  onError: _onError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    shortTermLimit: 2,
    shortTermWindowSec: 10,
    longTermLimit: 50,
    longTermWindowHours: 24,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (config) {
      setFormData({
        shortTermLimit: config.shortTermLimit,
        shortTermWindowSec: config.shortTermWindowSec,
        longTermLimit: config.longTermLimit,
        longTermWindowHours: config.longTermWindowHours,
      });
    }
  }, [config]);

  const handleChange = useCallback((field: string, value: string | undefined) => {
    const parsed = value !== undefined && value !== '' ? Number.parseInt(value, 10) : 0;
    setFormData((prev) => ({ ...prev, [field]: parsed }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  }, []);

  const handleShortTermLimitChange = useCallback((value: string | undefined) => handleChange('shortTermLimit', value), [
    handleChange,
  ]);
  const handleShortTermWindowChange = useCallback(
    (value: string | undefined) => handleChange('shortTermWindowSec', value),
    [handleChange],
  );
  const handleLongTermLimitChange = useCallback((value: string | undefined) => handleChange('longTermLimit', value), [
    handleChange,
  ]);
  const handleLongTermWindowChange = useCallback(
    (value: string | undefined) => handleChange('longTermWindowHours', value),
    [handleChange],
  );

  const handleSubmit = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (formData.shortTermLimit < 1) {
      newErrors.shortTermLimit = t('backoffice.search-rate-limit.form.validation.min-value', 'Must be at least 1');
    }
    if (formData.shortTermWindowSec < 1) {
      newErrors.shortTermWindowSec = t(
        'backoffice.search-rate-limit.form.validation.min-seconds',
        'Must be at least 1 second',
      );
    }
    if (formData.longTermLimit < 1) {
      newErrors.longTermLimit = t('backoffice.search-rate-limit.form.validation.min-value', 'Must be at least 1');
    }
    if (formData.longTermWindowHours < 1) {
      newErrors.longTermWindowHours = t(
        'backoffice.search-rate-limit.form.validation.min-hours',
        'Must be at least 1 hour',
      );
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    const payload: UpdateSearchRateLimitConfigRequest = {
      countryCode,
      ...formData,
      garageOverrides: config?.garageOverrides || {},
    };
    dispatch(updateSearchRateLimitConfigRequest(payload));
    setTimeout(() => {
      setLoading(false);
      onSuccess?.();
    }, SUCCESS_FEEDBACK_DELAY_MS);
  }, [countryCode, formData, config, dispatch, onSuccess, t]);

  const handleReset = useCallback(() => {
    if (config) {
      setFormData({
        shortTermLimit: config.shortTermLimit,
        shortTermWindowSec: config.shortTermWindowSec,
        longTermLimit: config.longTermLimit,
        longTermWindowHours: config.longTermWindowHours,
      });
      setErrors({});
    }
  }, [config]);

  return (
    <Flex direction={'column'} gap={15}>
      {/* Short-term limits */}
      <Flex direction={'column'} gap={5}>
        <Text type={'h4_paragraph'}>{t('backoffice.search-rate-limit.form.short-term.title', 'Short-Term Limit')}</Text>
        <Flex gap={15} align={'flex-start'}>
          <Flex direction={'column'} gap={5}>
            <Text type="base">
              {t('backoffice.search-rate-limit.form.max-searches', 'Max unique VIN/VRN searches')}
            </Text>
            <InputNumber
              min={1}
              value={formData.shortTermLimit}
              onChange={handleShortTermLimitChange}
              disabled={loading}
            />
            {errors.shortTermLimit && <Text type={'error'}>{errors.shortTermLimit}</Text>}
          </Flex>
          <Flex direction={'column'} gap={5}>
            <Text type="base">{t('backoffice.search-rate-limit.form.window-seconds', 'Window (seconds)')}</Text>
            <InputNumber
              min={1}
              value={formData.shortTermWindowSec}
              onChange={handleShortTermWindowChange}
              disabled={loading}
            />
            {errors.shortTermWindowSec && <Text type={'error'}>{errors.shortTermWindowSec}</Text>}
          </Flex>
        </Flex>
      </Flex>

      {/* Long-term limits */}
      <Flex direction={'column'} gap={5}>
        <Text type={'h4_paragraph'}>{t('backoffice.search-rate-limit.form.long-term.title', 'Long-Term Limit')}</Text>
        <Flex gap={15} align={'flex-start'}>
          <Flex direction={'column'} gap={5}>
            <Text type="base">
              {t('backoffice.search-rate-limit.form.max-searches', 'Max unique VIN/VRN searches')}
            </Text>
            <InputNumber
              min={1}
              value={formData.longTermLimit}
              onChange={handleLongTermLimitChange}
              disabled={loading}
            />
            {errors.longTermLimit && <Text type={'error'}>{errors.longTermLimit}</Text>}
          </Flex>
          <Flex direction={'column'} gap={5}>
            <Text type="base">{t('backoffice.search-rate-limit.form.window-hours', 'Window (hours)')}</Text>
            <InputNumber
              min={1}
              value={formData.longTermWindowHours}
              onChange={handleLongTermWindowChange}
              disabled={loading}
            />
            {errors.longTermWindowHours && <Text type={'error'}>{errors.longTermWindowHours}</Text>}
          </Flex>
        </Flex>
      </Flex>

      {/* Actions */}
      <MarginBox mt={5}>
        <Flex gap={10}>
          <YellowButton onClick={handleSubmit} disabled={loading} data-cy={'save-search-rate-limit-config'}>
            <Text type={'h4_paragraph'}>
              {loading
                ? t('backoffice.search-rate-limit.form.saving', 'Saving…')
                : t('backoffice.search-rate-limit.form.save', 'Save configuration')}
            </Text>
          </YellowButton>
          <WhiteButton onClick={handleReset} disabled={loading} data-cy={'reset-search-rate-limit-config'}>
            <Text type={'h4_paragraph'}>{t('backoffice.search-rate-limit.form.reset', 'Reset')}</Text>
          </WhiteButton>
        </Flex>
      </MarginBox>
    </Flex>
  );
};
